import { useCallback } from 'react'
import { useIntl } from 'react-intl'

import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import WalledContent from 'components/Common/WalledContent'
import OfferPageStickyFooter from 'components/OfferPage/OfferPageStickyFooter'
import DesktopRelatedTravelItems from 'components/OfferPage/RelatedTravelItems'
import Group from 'components/utils/Group'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import usePropertyStickyCashPrices from './usePropertyStickyCashPrices'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import {
  arePropertyRatesLoading,
  getPropertyCheapestHotelOnlyRateForDiscount,
  getPropertyCheapestRates,
  getPropertyFromDurationLabel,
  getPropertyRatesKey,
} from 'selectors/offerPage/offerPageSelectors'
import { shouldShowAnytimeRate } from 'selectors/offerPage/propertyOfferPageSelectors'

interface Props {
  property: App.Property.Property
  offer: App.HotelOffer
  onViewPackageOptions: () => void
  areAnytimeRates: boolean
  shouldShowAnytimeRate: boolean
  isLoading: boolean
  headlessMode: boolean
  durationLabel: string
  cheapestPropertyRates: [App.EnrichedPropertyRate | App.Property.AnytimeRate | undefined, App.EnrichedPropertyRate | undefined]
  cheapestHotelOnlyDiscountRate: App.EnrichedPropertyRate | undefined
  checkIn?: string
  checkOut?: string
  rooms: Array<App.Occupants>
}

function PropertyStickyFooter(props: Props) {
  const {
    property,
    offer,
    onViewPackageOptions,
    areAnytimeRates,
    shouldShowAnytimeRate,
    isLoading,
    headlessMode,
    durationLabel,
    cheapestPropertyRates,
    cheapestHotelOnlyDiscountRate,
    checkIn,
    checkOut,
    rooms,
  } = props

  const intl = useIntl()
  const dispatch = useOfferPageDispatch()

  const hideAnytimePrices = areAnytimeRates && !shouldShowAnytimeRate

  const onViewOptionsClick = useCallback(() => {
    if (hideAnytimePrices) {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    } else {
      dispatch({ type: OfferPageActions.closeFilter })
      onViewPackageOptions()
    }
  }, [hideAnytimePrices, dispatch, onViewPackageOptions])

  const [cheapestHotelOnlyRate] = cheapestPropertyRates

  const hasHotelPrice = !hideAnytimePrices && !!cheapestHotelOnlyRate

  const { hotelPrice, displayHotelPrice, displayMemberPrice, showMemberPrice, discountFetching } = usePropertyStickyCashPrices(
    cheapestHotelOnlyRate,
    cheapestHotelOnlyDiscountRate,
    checkIn,
    checkOut,
    rooms,
  )

  const value = cheapestHotelOnlyRate?.luxPlusTotals?.valuedUpToPrice || cheapestHotelOnlyRate?.totals.valuedUpToPrice || 0
  const { shouldShowValue, shouldShowDiscountPercent, highestDiscountPercent } = getHotelOfferDisplayedValue(
    // TODO: Checking this LPC offer flag isn't appropriate if we're dealing with an LPP rate.
    !!offer.bundledWithFlightsOnly,
    displayMemberPrice || displayHotelPrice,
    value,
    !!offer.shouldDisplayValue,
  )

  const callToActionCopy = hideAnytimePrices
    ? intl.formatMessage({
        id: 'v06NzwRL_kiScnF6_mnU',
        defaultMessage: 'Select dates',
        description: 'CTA text when dates need to be selected on property sticky footer',
      })
    : intl.formatMessage({
        id: 'cmfQfpT0XWMr5qGAdKp0',
        defaultMessage: 'View options',
        description: 'CTA text to view package options on property sticky footer',
      })

  return (
    <OfferPageStickyFooter
      visible
      callToActionCopy={callToActionCopy}
      onCTAClick={onViewOptionsClick}
      enforcedWalledGarden={property.walledGarden}
      offer={offer}
    >
      {!headlessMode && <DesktopRelatedTravelItems offer={offer} />}
      <WalledContent enforced={property.walledGarden}>
        {hasHotelPrice && hotelPrice > 0 && <Group direction="vertical">
          <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
          <LuxPlusPriceGuard
            showMemberPrice={showMemberPrice}
            fallbackPrice={<PriceRowPrice
              size="S"
              price={displayHotelPrice}
              saleUnit="total"
            />}
          >
            <PriceRowLuxPlusPrice
              size="M"
              memberPrice={displayMemberPrice}
              saleUnit="total"
              logoPlacement="leading"
            />
          </LuxPlusPriceGuard>
          {shouldShowValue && shouldShowDiscountPercent && <PriceRowValueDiscountWithCaption
            size="M"
            originalValue={value}
            discountPercentage={highestDiscountPercent}
          />}
          <LoadingIndicator visible={isLoading || discountFetching} floating opaque />
        </Group>}
        {hideAnytimePrices && <BodyTextBlock
          variant="medium"
          weight="bold"
          startIcon={<LineInfoCircleIcon />}
        >
          {intl.formatMessage({
            id: 'MvIbRvib9nw79uymZfx6',
            defaultMessage: 'Select dates to get the best price for this offer',
            description: 'Info message when dates aren’t selected on property sticky footer',
          })}
        </BodyTextBlock>}
      </WalledContent>
    </OfferPageStickyFooter>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  areAnytimeRates: !getPropertyRatesKey(state, appState),
  shouldShowAnytimeRate: shouldShowAnytimeRate(state, appState),
  isLoading: arePropertyRatesLoading(state, appState),
  headlessMode: appState.system.headlessMode,
  durationLabel: getPropertyFromDurationLabel(state, appState),
  cheapestPropertyRates: getPropertyCheapestRates(state, appState),
  cheapestHotelOnlyDiscountRate: getPropertyCheapestHotelOnlyRateForDiscount(state, appState),
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  rooms: state.rooms,
  open: !!state.activeFilter,
}), PropertyStickyFooter, preventRenderWithOpenFilter)
